import browserStorageService from '@/services/browserStorageService';
import AzureAuthRedirect from './AzureAuthRedirect';
import store from '@/store/index';
import utils from '@/common/utils';
import networkUtils from '@/common/networkUtils';

/**
 * @typedef AzureAuthProviderConfig
 * @type {object}
 * @property {string} type - the cloud auth provider type: azure
 * @property {string} clientId - azure clientId
 * @property {string} authority - azure authority
 */

export default {
  /**
   * @typedef AuthenticationResponse
   * @type {object}
   * @property {string} responseCode - isAuthenticated | loginRequired | externalSignInStarted
   * @property {string} token - the JWT app token (only when authenticated)
   * @property {string} refreshToken - the JWT refreshToken token (only when authenticated)
   * @property {number} expiresInSeconds - expiresInSeconds (only when authenticated)
   * @property {string} userName - user name (only when authenticated)
   * @property {string} employeeNr - employeeNr (only when authenticated)
   * @property {string} firstName - firstName (only when authenticated)
   * @property {string} lastName - lastName (only when authenticated)
   * @property {Array} groups - lastName (only when authenticated)
   * @property {boolean} rememberMe - if the login should be remembered  (only when authenticated)
   */

  /**
   * Authenticate user given the cloudAuthProvider which must be an azure specific
   * cloudAuthProvider stored in ui-config/customerFeatures/cloudAuthProvider in default or environment specific subelement.
   * In case there is a valid login stored, this token is retrieved without any other actions.
   * However in case no login has been found, the signin redirect flow is getting initialized,
   * that is the app is quit and called again after successful login. The app-lifecycle is re-started thus.
   * @param {AzureAuthProviderConfig} cloudAuthProviderConfig
   * @returns {AuthenticationResponse} an object containing a responseCode and credentials in success case
   */
  async authenticateUser(cloudAuthProviderConfig) {
    let isOnline = await networkUtils.isOnline({ commit: store.commit });
    console.log('authenticate user with isOnline=', isOnline);
    if (isOnline) {
      console.log('perform azure login in connected state');

      AzureAuthRedirect.init(cloudAuthProviderConfig);
      try {
        let tokenResponse = await AzureAuthRedirect.initiateLogin(cloudAuthProviderConfig);

        let idToken = tokenResponse.idToken;
        let accessToken = tokenResponse.accessToken;

        console.debug('Login tokenResponse', tokenResponse);

        browserStorageService.setAzureIdToken(idToken);
        browserStorageService.setAzureAccessToken(accessToken);

        return await this.acquireAppToken(cloudAuthProviderConfig, tokenResponse.account.username, idToken);
      } catch (err) {
        console.error('azure login failed.', err);
        AzureAuthRedirect.signIn();
        throw err;
      }
    } else {
      console.log('perform azure login in disconnected state => use stored idToken to get nxg-token');
      let idToken = browserStorageService.getAzureIdToken();
      let userName = browserStorageService.getUser().userName;
      try {
        return await this.acquireAppToken(cloudAuthProviderConfig, userName, idToken);
      } catch (err) {
        console.error('azure login in disconnected state failed', err);
        return null;
      }
    }
  },

  /**
   * Log out user.
   * @param {AzureAuthProviderConfig} cloudAuthProviderConfig
   */
  async signOut(cloudAuthProviderConfig) {
    if (await networkUtils.isOnline({ commit: store.commit })) {
      console.log('perform azure signOut in connected state');

      AzureAuthRedirect.init(cloudAuthProviderConfig);
      await AzureAuthRedirect.signOut(cloudAuthProviderConfig);
      browserStorageService.clearAzureIdToken();
      browserStorageService.clearAzureAccessToken();
    } else {
      browserStorageService.clearAzureIdToken();
      browserStorageService.clearAzureAccessToken();
    }
    if (sessionStorage.getItem('msal.interaction.status')) {
      sessionStorage.removeItem('msal.interaction.status');
    }
  },
  /**
   * @typedef TokenResponse
   * @type {Object}
   * @property {string} token -the öV-Pad app token
   * @property {string} msg - a message from the profile/login action
   */
  /**
   * Acquire the öV-Pad NxG app token by initiating the login method (using the azure id-token as password)
   * @param {*} cloudAuthProviderConfig - the auth provider config
   * @param {*} username - the azure username
   * @param {*} idToken - the azure id-token
   * @returns {TokenResponse} the token response
   */
  async acquireAppToken(cloudAuthProviderConfig, username, idToken) {
    const tenant = store.state.profile.customerRelations.tenants[0];
    try {
      let tokenResponse = await store.dispatch('profile/login', { tenant: tenant.value, username, password: 'azure-pw', token: idToken });
      console.log('acquireAppToken', tokenResponse);
      return tokenResponse;
    } catch (err) {
      if (err.response.status === 403) {
        console.warn('WebApp login not allowed.');
        localStorage.setItem('azure.login-error', 'Dieser Benutzer hat keinen Zugriff auf die WebApp!');
        return err;
      }
      if (err.response.status === 401) {
        console.warn('loginWithToken failed', err.msg, err.error);

        localStorage.removeItem('azure.auto_login');
        localStorage.removeItem('azure.id-token');
        localStorage.removeItem('azure.access-token');

        //sessionStorage.clear();

        localStorage.setItem('azure.login-error', 'Azure Login fehlgeschlagen.');

        if (cloudAuthProviderConfig?.manualLoginEnabled) {
          this.$router.push(`/`);
        }
      }
      return;
    }
  },
};
